//** Import Modules */
import React, { useState, useEffect } from "react";
import PageTitleNoAdd from "../../components/header/PageTitleNoAdd";
import axiosWithAuth from "../../utils/axiosWithAuth";
import { useSelector } from "react-redux";
import CheckoutSteps from "./CheckoutSteps";
import { Affix, Alert, Spin, Divider } from "antd";
import { calculateCheckoutTaxes } from "../../utils/checkoutTaxes";
import { isRestaurantOrder } from "../../hooks/useCreateOrder";

import { readCheckoutTip } from "../../utils/checkoutTip";

import OrderTaxRows from "../../common/OrderTaxRows";

export default function CustomerCheckout({ type }) {
  // Get the logged in user data
  const user = useSelector((state) => state.user.user);

  // Set states
  const [taxes, setTaxes] = useState(null);
  const [checkoutReady, setCheckoutReady] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [cartItems, setCartItems] = useState([]);
  const [subTotal, setSubTotal] = useState(0);
  const [amountTotal, setAmountTotal] = useState(0);
  const [tipAmount, setTipAmount] = useState(0);
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [deliveryType, setDeliveryType] = useState("Pickup");
  const [customerInfo, setCustomerInfo] = useState({});
  const [userInfo, setUserInfo] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

  // Get URL Info
  const URLQueryVars = new URL(document.location).searchParams;
  const checkoutType = URLQueryVars.get("type");
  const membershipID = URLQueryVars.get("membershipID");
  const couponCode = URLQueryVars.get("cpc");

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check what type of product we buying. If it is a membership, use the membership info instead of shopping cart
  useEffect(() => {
    let cancelled = false;
    setCheckoutReady(false);
    setCheckoutError("");
    if (checkoutType === "membership") {
      const APIURL = `/api/memberships/membership/${membershipID}`;

      axiosWithAuth()
        .get(APIURL)
        .then((res) => {
          if (cancelled) return;
          let productItem;
          let getSubTotal;

          if (couponCode === "jJnGdVrb") {
            productItem = {
              itemID: res.data.id,
              itemName: `${res.data.name} - 3mon Trial`,
              itemPrice: res.data.cycle_payment,
              stripeProductID: res.data.stripe_product_id,
              itemCycle: res.data.cycle_period,
              itemInitialPayment: res.data.initial_payment,
            };

            getSubTotal = 0;
          } else {
            productItem = {
              itemID: res.data.id,
              itemName: res.data.name,
              itemPrice: res.data.cycle_payment,
              stripeProductID: res.data.stripe_product_id,
              itemCycle: res.data.cycle_period,
              itemInitialPayment: res.data.initial_payment,
            };

            getSubTotal = productItem.itemPrice;
          }

          setCartItems([productItem]);
          setSubTotal(getSubTotal);
          setAmountTotal(getSubTotal <= 0 ? 0 : getSubTotal);
          setCheckoutReady(true);
        })
        .catch((err) => {
          if (!cancelled) {
            setCheckoutError(
              "Unable to load checkout. Please reload to try again.",
            );
          }
          console.error("membership checkout error", err.response);
        });
    } else {
      const productItems = localStorage.getItem("cart");
      const productItemsList = JSON.parse(productItems) || [];
      const hasRestaurantOrder = isRestaurantOrder(productItemsList);
      const checkoutTip = hasRestaurantOrder
        ? readCheckoutTip(localStorage.getItem("checkoutTip"), productItemsList)
        : 0;
      setTipAmount(checkoutTip);
      console.log("[Bizzll] Checkout tip:", { tipAmount: checkoutTip });

      const userInfoStorage = localStorage.getItem("userInfo");
      const userInfo = JSON.parse(userInfoStorage);

      const deliveryPriceStorage = localStorage.getItem("deliveryPrice");
      const deliveryPrice = hasRestaurantOrder
        ? Number(JSON.parse(deliveryPriceStorage)) || 0
        : 0;
      setDeliveryPrice(deliveryPrice);

      const deliveryTypeStorage = localStorage.getItem("deliveryType");
      const deliveryType = hasRestaurantOrder && deliveryTypeStorage
        ? JSON.parse(deliveryTypeStorage)
        : "Pickup";
      setDeliveryType(deliveryType);

      const getSubTotal = productItemsList.reduce(
        (acc, item) => acc + Number(item.itemTotal),
        0,
      );

      setUserInfo(userInfo);
      setCartItems(productItemsList);
      setSubTotal(getSubTotal);

      const businessID = productItemsList[0]?.businessID;
      if (
        !businessID ||
        productItemsList.some(
          (item) => String(item.businessID) !== String(businessID),
        )
      ) {
        setCheckoutError("Please check out with items from one business.");
      } else {
        axiosWithAuth()
          .get(`/api/business/${businessID}`)
          .then(({ data }) => {
            if (cancelled) return;
            const calculatedTaxes = calculateCheckoutTaxes(
              getSubTotal,
              deliveryPrice,
              data,
            );
            setTaxes(calculatedTaxes);
            setAmountTotal(
              Math.round((calculatedTaxes.total + checkoutTip) * 100) / 100,
            );
            setCheckoutReady(true);
          })
          .catch(() => {
            if (!cancelled) {
              setCheckoutError(
                "Unable to load business taxes. Please reload to try again.",
              );
            }
          });
      }
    }

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutType, membershipID]);

  // Create a Customer on Stripe under BIZZLL's account. If it exists, just get the customer details
  useEffect(() => {
    if (user.id) {
      const APIURL = `/api/stripe/customer/create`;

      const payload = {
        userEmail: user.email,
        userName: `${user.fname} ${user.lname}`,
        referralData: user.referral,
      };

      axiosWithAuth()
        .post(APIURL, payload)
        .then((res) => {
          console.log("stripe customer create response", res);
          setCustomerInfo(res.data);
        })
        .catch((err) => {
          console.error("stripe customer create error", err.response);
        });
    } else {
      console.warn("stripe customer create skipped: missing user.id", user);
    }
  }, [user]);

  return (
    <div id="checkout-page" className="page-content">
      <PageTitleNoAdd pageTitle="Checkout" />

      {checkoutError ? (
        <Alert type="error" message={checkoutError} showIcon />
      ) : !checkoutReady ? (
        <Spin tip="Calculating order total..." />
      ) : (
        <div className="checkout-details page-wrapper">
          <div className="checkout-steps">
            <CheckoutSteps
              user={user}
              customerInfo={customerInfo}
              cartItems={cartItems}
              userInfo={userInfo}
              deliveryPrice={deliveryPrice}
              deliveryType={deliveryType}
              checkoutType={checkoutType}
              subTotal={subTotal}
              amountTotal={amountTotal}
              taxes={taxes}
              tipAmount={tipAmount}
              couponCode={couponCode}
            />
          </div>

          {isMobile ? (
            <div className="checkout-summary">
              <div className="inner-content">
                <div className="summary-title">Order Summary</div>

                <div className="cart-items-list">
                  {cartItems.map((item) => {
                    return (
                      <div className="cart-item" key={item.itemName}>
                        <div className="item-name">
                          {item.itemName}
                          {item.itemQuantity && ` x ${item.itemQuantity}`}
                        </div>

                        <div className="item-price">
                          {item.itemPrice <= 0 ? (
                            <>Free</>
                          ) : (
                            <>
                              ${item.itemPrice}
                              {item.itemCycle && <> / {item.itemCycle}</>}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="cart-subtotal summary-section">
                  {cartItems.some(
                    (item) => item.itemType === "restaurant_product",
                  ) && (
                    <div className="summary-row">
                      <div className="label">Delivery Price</div>
                      <div className="value">${deliveryPrice}</div>
                    </div>
                  )}

                  <div className="summary-row">
                    <div className="label">Order amount</div>
                    <div className="value">${subTotal.toFixed(2)}</div>
                  </div>
                </div>

                {subTotal > 0 && checkoutType !== "membership" && (
                  <div className="extra-fees summary-section">
                    <OrderTaxRows summary={taxes} />
                  </div>
                )}

                {checkoutType !== "membership" && isRestaurantOrder(cartItems) && (
                  <div className="cart-tip summary-section">
                    <div className="summary-row">
                      <div className="label">Tip</div>
                      <div className="value">${tipAmount.toFixed(2)}</div>
                    </div>
                  </div>
                )}

                <div className="cart-total summary-section">
                  <div className="summary-row">
                    <div className="label">Total</div>
                    <div className="value">${amountTotal.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Affix offsetTop={100} className="checkout-summary">
              <div className="inner-content">
                <div className="summary-title">Order Summary</div>

                <div className="cart-items-list">
                  {cartItems.map((item) => {
                    return (
                      <div className="cart-item" key={item.itemName}>
                        <div className="item-name">
                          {item.itemName}
                          {item.itemQuantity && ` x ${item.itemQuantity}`}
                        </div>

                        <div className="item-price">
                          {item.itemPrice <= 0 ? (
                            <>Free</>
                          ) : (
                            <>
                              ${item.itemPrice}
                              {item.itemCycle && <> / {item.itemCycle}</>}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="cart-subtotal summary-section">
                  <div className="summary-row">
                    <div className="label">Order amount</div>
                    <div className="value">${subTotal.toFixed(2)}</div>
                  </div>
                  {cartItems.some(
                    (item) => item.itemType === "restaurant_product",
                  ) && (
                    <div className="summary-row">
                      <div className="label">Delivery Price</div>
                      <div className="value">${deliveryPrice}</div>
                    </div>
                  )}

                  <Divider />

                  <div className="summary-row">
                    <div className="label">Subtotal</div>
                    <div className="value">
                      ${(subTotal + deliveryPrice).toFixed(2)}
                    </div>
                  </div>
                </div>

                {subTotal > 0 && checkoutType !== "membership" && (
                  <div className="extra-fees summary-section">
                    <OrderTaxRows summary={taxes} />
                  </div>
                )}

                {checkoutType !== "membership" && isRestaurantOrder(cartItems) && (
                  <div className="cart-tip summary-section">
                    <div className="summary-row">
                      <div className="label">Tip</div>
                      <div className="value">${tipAmount.toFixed(2)}</div>
                    </div>
                  </div>
                )}

                <div className="cart-total summary-section">
                  <div className="summary-row">
                    <div className="label">Total</div>
                    <div className="value">${amountTotal.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </Affix>
          )}
        </div>
      )}
    </div>
  );
}
